/**
 * Admin Users Page
 *
 * Displays a paginated, searchable table of all users with aggregated stats.
 * Each user row links to a detailed user profile page.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAdminUsers } from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import type { AdminUsersListResponse, AdminUserListItem } from '../types/admin.js';

type SortField = 'created_at' | 'username' | 'xrpl_address' | 'email';
type SortOrder = 'asc' | 'desc';

export function AdminUsersPage() {
  const [data, setData] = useState<AdminUsersListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination and filters
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState<SortField>('created_at');
  const [order, setOrder] = useState<SortOrder>('desc');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getAdminUsers({
        page,
        limit,
        search: search || undefined,
        sort,
        order,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, sort, order]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('desc');
    }
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const SortHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sort === field && (
          <span className="text-blue-600">
            {order === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Users</h1>
          <p className="mt-2 text-sm text-gray-600">
            {data ? `${data.pagination.total.toLocaleString()} total users` : 'Loading...'}
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by address, username, or email..."
              className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                  setPage(1);
                }}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadUsers} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && !data && (
          <div className="bg-white rounded-lg shadow p-6 animate-pulse">
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        )}

        {/* Users Table */}
        {data && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortHeader field="xrpl_address" label="Address" />
                    <SortHeader field="username" label="Username" />
                    <SortHeader field="email" label="Email" />
                    <SortHeader field="created_at" label="Joined" />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alerts
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.users.map((user) => (
                    <UserRow key={user.id} user={user} formatDate={formatDate} truncateAddress={truncateAddress} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination.totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data.pagination.total)} of {data.pagination.total} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {data.pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(data.pagination.totalPages)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {data && data.users.length === 0 && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500">No users found matching your search.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function UserRow({
  user,
  formatDate,
  truncateAddress,
}: {
  user: AdminUserListItem;
  formatDate: (date: string) => string;
  truncateAddress: (addr: string) => string;
}) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-4 whitespace-nowrap">
        <Link
          to={`/admin/users/${user.id}`}
          className="text-blue-600 hover:text-blue-800 font-mono text-sm"
          title={user.xrplAddress}
        >
          {truncateAddress(user.xrplAddress)}
        </Link>
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          {user.avatarUrl && (
            <img
              src={user.avatarUrl}
              alt=""
              className="w-6 h-6 rounded-full"
            />
          )}
          <span className="text-sm text-gray-900">
            {user.username || <span className="text-gray-400 italic">No username</span>}
          </span>
        </div>
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm">
        {user.email ? (
          <div className="flex items-center gap-1">
            <span className="text-gray-900">{user.email}</span>
            {user.emailConfirmed && (
              <span className="text-green-600" title="Email confirmed">✓</span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 italic">No email</span>
        )}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(user.createdAt)}
      </td>
      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
        {user.stats.alertCount}
      </td>
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex gap-1">
          {user.isAdmin && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              Admin
            </span>
          )}
          {user.isVerified && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              Verified
            </span>
          )}
          {user.hasXamanToken && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              Xaman
            </span>
          )}
          {user.stats.activeSessionCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              Active
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
